import { useRef } from 'react'
import { gsap, SplitText, useGSAP, isTouch } from '../lib/gsap'
import { useLang } from '../lib/useLang'
import RichText from '../lib/RichText'
import { ABOUT, HIGHLIGHTS, STACK, UI } from '../data/content'
import LiquidGlass from '../components/LiquidGlass'
import Logo from '../components/Logo'
import LocalTime from '../components/LocalTime'
import portrait from '../midia/dudu-portrait.jpeg'

const ICONS = {
  wallet: 'M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Zm13 5h.01',
  chart: 'M4 19V5m0 14h16M8 15l3-4 3 2 5-6',
  shield: 'M12 3 5 6v5c0 4.5 3 8.5 7 10 4-1.5 7-5.5 7-10V6l-7-3Zm-3 9 2 2 4-4',
  spark: 'M12 3v4m0 10v4M3 12h4m10 0h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6',
}

function TiltFrame({ children }) {
  const ref = useRef(null)
  useGSAP(() => {
    if (isTouch()) return
    const el = ref.current
    const rx = gsap.quickTo(el, 'rotateX', { duration: 0.8, ease: 'power3.out' })
    const ry = gsap.quickTo(el, 'rotateY', { duration: 0.8, ease: 'power3.out' })
    const move = (e) => {
      const r = el.getBoundingClientRect()
      ry(((e.clientX - r.left) / r.width - 0.5) * 14)
      rx(-((e.clientY - r.top) / r.height - 0.5) * 14)
    }
    const leave = () => {
      rx(0)
      ry(0)
    }
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerleave', leave)
    return () => {
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerleave', leave)
    }
  })
  return (
    <div style={{ perspective: 1200 }}>
      <div ref={ref} className="relative" style={{ transformStyle: 'preserve-3d' }}>
        {children}
      </div>
    </div>
  )
}

function Stat({ value, suffix = '', label }) {
  return (
    <LiquidGlass data-stat radius={22} bezel={16} refraction={34} className="flex flex-1 flex-col gap-1 p-5">
      <span className="font-display text-5xl font-semibold tracking-[-0.04em] text-white">
        <span data-count={value}>0</span>
        <span className="text-gradient">{suffix}</span>
      </span>
      <span className="text-xs leading-snug text-mist">{label}</span>
    </LiquidGlass>
  )
}

function SpotlightCard({ item, index }) {
  const { t } = useLang()
  const ref = useRef(null)
  const move = (e) => {
    const r = ref.current.getBoundingClientRect()
    ref.current.style.setProperty('--x', `${e.clientX - r.left}px`)
    ref.current.style.setProperty('--y', `${e.clientY - r.top}px`)
  }
  return (
    <div
      ref={ref}
      data-hl
      onPointerMove={move}
      className="group relative overflow-hidden rounded-[24px] bg-white/[0.025] p-6 ring-1 ring-white/[0.08] transition-colors duration-500 hover:bg-white/[0.04]"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: 'radial-gradient(380px circle at var(--x) var(--y), rgba(45,92,255,0.22), transparent 45%)' }}
      />
      <div className="relative flex items-start justify-between">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand shadow-[0_0_24px_-4px_rgba(45,92,255,0.8),inset_0_1px_0_rgba(255,255,255,0.35)]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d={ICONS[item.icon]} />
          </svg>
        </span>
        <span className="label text-white/25">0{index + 1}</span>
      </div>
      <h4 className="relative mt-6 font-display text-xl font-semibold leading-tight tracking-[-0.02em] text-white">{t(item.title)}</h4>
      <p className="relative mt-3 text-sm leading-relaxed text-mist">{t(item.body)}</p>
    </div>
  )
}

export default function About({ projectCount = 5 }) {
  const root = useRef(null)
  const { t, lang } = useLang()

  useGSAP(
    () => {
      const title = SplitText.create('[data-about-title]', { type: 'words,chars', mask: 'chars' })
      gsap.from(title.chars, { yPercent: 110, stagger: 0.03, scrollTrigger: { trigger: '[data-about-title]', start: 'top 85%' } })

      const paras = SplitText.create('[data-para]', { type: 'lines', mask: 'lines', autoSplit: true })
      gsap.from(paras.lines, { yPercent: 100, duration: 1.2, stagger: 0.06, scrollTrigger: { trigger: '[data-paras]', start: 'top 80%' } })

      gsap.from('[data-photo]', {
        clipPath: 'inset(100% 0% 0% 0% round 40px)',
        duration: 1.8,
        ease: 'expo.inOut',
        scrollTrigger: { trigger: '[data-photo]', start: 'top 85%' },
      })
      gsap.fromTo('[data-photo-img]', { yPercent: -8, scale: 1.15 }, { yPercent: 8, scale: 1.05, ease: 'none', scrollTrigger: { trigger: '[data-photo]', start: 'top bottom', end: 'bottom top', scrub: true } })
      gsap.from('[data-badge]', { scale: 0, opacity: 0, stagger: 0.12, duration: 1.2, ease: 'elastic.out(1, 0.6)', scrollTrigger: { trigger: '[data-photo]', start: 'top 60%' } })

      gsap.utils.toArray('[data-count]').forEach((el) => {
        const end = Number(el.dataset.count)
        const o = { v: 0 }
        gsap.to(o, {
          v: end,
          duration: 2,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 90%' },
          onUpdate: () => (el.textContent = Math.round(o.v)),
        })
      })
      gsap.from('[data-stat]', { y: 40, opacity: 0, stagger: 0.1, scrollTrigger: { trigger: '[data-stats]', start: 'top 88%' } })
      gsap.from('[data-hl]', { y: 60, opacity: 0, stagger: 0.1, duration: 1.3, scrollTrigger: { trigger: '[data-hls]', start: 'top 85%' } })
      return () => {
        title.revert()
        paras.revert()
      }
    },
    { scope: root, dependencies: [lang], revertOnUpdate: true },
  )

  return (
    <section id="about" ref={root} className="relative overflow-hidden py-28 sm:py-40">
      <div className="pointer-events-none absolute -left-60 top-1/3 h-[700px] w-[700px] rounded-full opacity-30 blur-[140px]" style={{ background: '#2D5CFF' }} />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-10">
        <div className="flex flex-col gap-5">
          <span className="label text-mist">(05) — {t(UI.aboutTitle)}</span>
          <h2 key={lang} data-about-title className="font-display text-[clamp(3rem,8vw,7.5rem)] font-semibold leading-[0.88] tracking-[-0.045em] text-white">
            {t(UI.aboutTitle)}
          </h2>
        </div>

        <div className="mt-16 grid gap-14 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
          {/* Portrait */}
          <div className="relative mx-auto w-full max-w-[480px] lg:sticky lg:top-28 lg:self-start">
            <TiltFrame>
              <LiquidGlass radius={40} bezel={28} refraction={70} blur={4} className="p-3">
                <div data-photo className="relative aspect-[4/5] overflow-hidden rounded-[30px] bg-gradient-to-b from-[#1a2250] to-ink-2">
                  <img data-photo-img src={portrait} alt="Eduardo Brancalhão" className="absolute inset-0 h-full w-full object-cover object-[50%_25%]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                    <div>
                      <p className="font-display text-2xl font-semibold tracking-[-0.02em] text-white">Eduardo Brancalhão</p>
                      <p className="text-sm text-fog">{t(UI.role)}</p>
                    </div>
                    <Logo variant="white" className="h-8 w-8 opacity-90" />
                  </div>
                </div>
              </LiquidGlass>
              <LiquidGlass data-badge radius={999} bezel={14} refraction={28} className="absolute -right-3 top-10 flex items-center gap-2 px-4 py-2.5 sm:-right-8" style={{ transform: 'translateZ(60px)' }}>
                <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_#34d399]" />
                <span className="flex flex-col leading-tight">
                  <span className="text-xs text-white">{t(UI.based)}</span>
                  <LocalTime className="label text-[0.58rem] text-mist" />
                </span>
              </LiquidGlass>
              <LiquidGlass data-badge radius={999} bezel={14} refraction={28} className="absolute -left-3 bottom-28 flex items-center gap-2 px-4 py-2.5 sm:-left-8" style={{ transform: 'translateZ(80px)' }}>
                <Logo className="h-5 w-5" />
                <span className="font-brand text-lg text-white">Dudu</span>
              </LiquidGlass>
            </TiltFrame>
          </div>

          {/* Story */}
          <div className="flex flex-col gap-12">
            <div data-paras className="flex flex-col gap-6">
              {ABOUT.map((p, i) => (
                <p key={`${lang}-${i}`} data-para className="text-lg leading-relaxed text-mist sm:text-xl">
                  <RichText text={t(p)} />
                </p>
              ))}
            </div>

            <div data-stats className="flex flex-col gap-3 sm:flex-row">
              <Stat value={4} suffix="+" label={t(UI.years)} />
              <Stat value={projectCount} label={t(UI.projectsCount)} />
              <Stat value={STACK.length} suffix="+" label={t(UI.toolsCount)} />
            </div>

            <div className="flex flex-col gap-5">
              <h3 className="font-display text-3xl font-semibold tracking-[-0.03em] text-white">{t(UI.highlights)}</h3>
              <div data-hls className="grid gap-4 sm:grid-cols-2">
                {HIGHLIGHTS.map((h, i) => (
                  <SpotlightCard key={i} item={h} index={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
